import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link as MuiLink,
} from '@mui/material';
import axios from 'axios';
import { getAppCheckHeaders } from '../firebase';
import { useSearchParams, Link } from 'react-router-dom';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';

const API_BACKEND = process.env.REACT_APP_SHOPKEEP_BACKEND || 'http://localhost:3001';

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setSubmitStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    try {
      const headers = await getAppCheckHeaders();
      const response = await axios.post(`${API_BACKEND}/api/mailing-list/unsubscribe`, { email: trimmed }, { headers });
      if (response.data.success) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
        setErrorMessage(response.data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setSubmitStatus('error');
      if (err.response?.status === 429) {
        setErrorMessage('Too many requests. Please try again later.');
      } else if (err.response?.data?.error) {
        setErrorMessage(err.response.data.error);
      } else {
        setErrorMessage('Unable to process your request at this time. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <UnsubscribeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Unsubscribe from Mailing List
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your email address below to unsubscribe from our newsletter.
          </Typography>

          {submitStatus === 'success' ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>
                You have been successfully unsubscribed from our mailing list.
              </Alert>
              <MuiLink component={Link} to="/" underline="hover" variant="body2">
                Return to Reseller Pro
              </MuiLink>
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Email Address"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (submitStatus === 'error') {
                    setSubmitStatus('idle');
                    setErrorMessage('');
                  }
                }}
                error={submitStatus === 'error'}
                helperText={submitStatus === 'error' ? errorMessage : ''}
                disabled={isSubmitting}
                aria-label="Email address to unsubscribe"
                sx={{ mb: 2 }}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                sx={{ textTransform: 'none' }}
              >
                {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Unsubscribe'}
              </Button>
            </form>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Unsubscribe;
